import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  addToast: () => {},
  removeToast: () => {},
});

export const useToast = () => useContext(ToastContext);

const typeStyles: Record<ToastType, { icon: string; border: string; iconColor: string }> = {
  success: {
    icon: '\u2713',
    border: 'border-accent-success/30',
    iconColor: 'text-accent-success',
  },
  error: {
    icon: '\u2717',
    border: 'border-accent-danger/30',
    iconColor: 'text-accent-danger',
  },
  warning: {
    icon: '!',
    border: 'border-accent-warning/30',
    iconColor: 'text-accent-warning',
  },
  info: {
    icon: 'i',
    border: 'border-accent-info/30',
    iconColor: 'text-accent-info',
  },
};

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);
  const style = typeStyles[toast.type];

  useEffect(() => {
    const duration = toast.duration ?? 4000;
    const exitTimer = setTimeout(() => setIsExiting(true), duration - 300);
    const removeTimer = setTimeout(() => onRemove(toast.id), duration);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [toast, onRemove]);

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-card border bg-bg-secondary/95 backdrop-blur-sm
        shadow-lg min-w-[320px] max-w-[420px]
        ${style.border}
        ${isExiting ? 'animate-toast-out' : 'animate-toast-in'}
      `}
    >
      {/* Icon */}
      <span
        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${style.iconColor} bg-bg-surface`}
      >
        {style.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-txt-primary font-sans">{toast.title}</p>
        {toast.message && (
          <p className="text-xs text-txt-muted mt-0.5 font-sans">{toast.message}</p>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 text-txt-muted hover:text-txt-primary transition-colors text-lg leading-none"
      >
        &times;
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
